import ContentHubStoryListing from "@app/layouts/ContentHub/ContentHubStoryListing";
import ContentHubStoryListingLayout from "@app/layouts/ContentHub/ContentHubStoryListingLayout";
import AdSettings from "@app/utils/AdSettings";
import { mapTeaserDataToLatestProps } from "@app/utils/mapTeaserDataToProps";
import { configSite, configTechshow } from "@pub/config";
import {
  Ads,
  Arrow,
  PageShare,
  ResponsiveImage,
  Typography,
} from "@sphtech/dmg-design-system";
import { NoSSR } from "@sphtech/web2-core/components";
import cx from "classnames";
import { ReactNode } from "react";
import { useLocation } from "react-router";

import styles from "./TechshowPortal.module.css";
import TechshowPortalBlock from "./TechshowPortalBlock";
import { TechshowPortalViewLayoutProps } from "./types";
import {
  BASE_BROCHURE_PATH,
  BASE_BROCHURE_VIEW_PATH,
  BASE_TECHSHOW_PATH,
  buildFilterSegment,
  joinPaths,
} from "./utils";

export default function TechshowPortalViewLayout({
  headline,
  coverImage,
  path,
  displaySetting,
  techshowInfos,
  topStories,
  listedStories,
  tags,
  brochureImages,
  brochureNext,
  brochurePrev,
  selectedBrand,
  selectedCategory,
}: TechshowPortalViewLayoutProps): ReactNode {
  const { Body, Heading } = Typography;
  const originalCrop = coverImage?.crops.original;
  const location = useLocation();

  const ignoreIds = [...topStories, ...listedStories]
    .map((item) => item.id)
    .filter((id): id is string => !!id);

  const tagSlugs = tags?.map((tag) => tag.slug) || [];

  const brochureViewUrlFilter = buildFilterSegment(
    selectedCategory,
    selectedBrand,
  );

  //get first one for now
  const techshowInfo = techshowInfos?.[0];

  return (
    <>
      <AdSettings
        //StoryData={storyData}
        pageTitle={headline || ""}
        pageType="techshowportal"
        sectionName={headline || ""}
        currentLocation={location}
        adUnits="lb1,lb2,imu1"
      />
      {originalCrop && (
        <div
          className={cx(
            styles.headingBackgroundContainer,
            styles.subpageHeadingBackgroundContainer,
          )}
        >
          <ResponsiveImage {...originalCrop} displayWidth={2000} />
        </div>
      )}

      <div
        className={cx(
          styles.bodyContainer,
          styles.gutter,
          styles.techshowBrochuresMasthead,
        )}
      >
        <div className={styles.techshowBrochuresHeader}>
          <div className={styles.techshowBreadcrumbs}>
            <a className={styles.cta} href={BASE_TECHSHOW_PATH}>
              Tech Show Portal
            </a>
            <span className={styles.separator}> | </span>
            <a
              className={styles.cta}
              href={joinPaths(BASE_BROCHURE_PATH, brochureViewUrlFilter)}
            >
              Brochures
            </a>
          </div>
          <Heading.SectionTitle>
            {techshowInfo?.title || configTechshow.brochureView.title}
          </Heading.SectionTitle>
          <Body>{configTechshow.brochureView.description}</Body>
        </div>
        <PageShare
          label="Share&nbsp;this"
          url={`${configSite.address}${location.pathname}`}
          title={headline}
          size={configSite.pageShare?.size}
          media={configSite.pageShare?.media}
          fill={configSite.pageShare?.fill}
        />
      </div>

      <div
        className={cx(
          styles.techshowContainer,
          styles.bodyContainer,
          styles.gutterBottom,
        )}
      >
        {brochurePrev && (
          <a
            href={joinPaths(
              BASE_BROCHURE_VIEW_PATH,
              `${brochurePrev.id}`,
              brochureViewUrlFilter,
              "?ref=brochureleft",
            )}
            className={styles.brochureNavButton}
          >
            <Arrow direction="left" />
          </a>
        )}
        {brochureImages.map((brochureImage, key) => {
          const brochureImageOriginalUrl =
            brochureImage.fields?.["original-caas"]?.url || "";
          const brochureImageWidth =
            brochureImage.fields?.dimensions?.find(
              (field) => field.key === "width",
            )?.value || "0";
          const brochureImageHeight =
            brochureImage.fields?.dimensions?.find(
              (field) => field.key === "height",
            )?.value || "0";
          return (
            <a
              key={key}
              href={brochureImageOriginalUrl}
              target="_blank"
              className={styles.brochureImage}
              rel="noreferrer"
            >
              <ResponsiveImage
                src={brochureImageOriginalUrl}
                srcWidth={Number(brochureImageWidth)}
                srcHeight={Number(brochureImageHeight)}
                alt={brochureImage.brochureTitle || ""}
              />
              <div className={styles.brochureTitle}>
                {brochureImage.brochureTitle}
              </div>
            </a>
          );
        })}
        {brochureNext && (
          <a
            href={joinPaths(
              BASE_BROCHURE_VIEW_PATH,
              `${brochureNext.id}`,
              brochureViewUrlFilter,
              "?ref=brochureright",
            )}
            className={styles.brochureNavButton}
          >
            <Arrow direction="right" />
          </a>
        )}
      </div>

      {/* {listedStories story listing} */}
      <div
        className={cx(
          styles.backgroundFill,
          styles.secondaryContentContainer,
          styles.gutterTopOnly,
        )}
      >
        <div className={styles.techshowRelated}>
          <TechshowPortalBlock
            data={{
              id: "",
              name: headline,
              path: path,
              tagSlugs: tagSlugs,
              topStories: topStories.map((teaser) =>
                mapTeaserDataToLatestProps(teaser, ""),
              ),
              displaySetting,
            }}
            from={0}
            limit={5}
          />
        </div>
        <div
          data-layout-position="midcontenthub"
          className={cx(
            styles.gutterBottom,
            styles.secondaryContentContainer,
            styles.contentBlockListingContainer,
          )}
        >
          <Ads.AdsMidContent />
          <ContentHubStoryListingLayout
            data={{
              listedStories: listedStories.map((teaser) =>
                mapTeaserDataToLatestProps(teaser, ""),
              ),
            }}
            CustomListingLayout={undefined}
          />
          <NoSSR>
            <ContentHubStoryListing
              data={{
                tagSlugs: tagSlugs.join(","),
                limit: 6,
                ignoreIds: ignoreIds,
                lb1: <Ads.AdsMidContent />,
              }}
              CustomListingLayout={undefined}
            />
          </NoSSR>
        </div>
      </div>
    </>
  );
}
